/**
 * useListActions — GENERYCZNA integracja akcji list (PHASE 25 Faza 1).
 *
 * Jedno miejsce spinające: resolver widoczności (katalog ∩ kartoteka ∩ moduły ∩
 * uprawnienia) + WYKONAWCA (czyta „sposób wykonania" z katalogu i rozgłasza po ID
 * na istniejące endpointy) + potwierdzenia akcji odwracalnych/niszczących +
 * menu kontekstowe (prawy klik). Dzięki temu każda lista to kilka linii zamiast
 * ~60 (patrz Kontrahenci) i jest JEDNO miejsce na audyt/bezpieczeństwo wykonania.
 *
 * Escape-hatch `handlers`: część endpointów ma niespójne kontrakty (np. eksport
 * kontrahentów oczekuje `contractorIds`, nie `ids`; import otwiera modal strony).
 * Dla takich akcji strona podaje własny handler — reszta idzie generykiem z katalogu.
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  type CustomActionButton,
  LIST_ACTION_CATALOG,
  type ListActionConfig,
  type ListActionDef,
  type ListActionDefinitionDTO,
  type ListActionId,
  type ListActionKartotekaKey,
} from '@saas/shared';
import { type QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  listActionLucideIcon,
  ListActionsContextMenu,
  type RowActionItem,
} from '@/components/configurable-table';
import { CustomActionButtonBar } from '@/components/configurable-table/CustomActionButtonBar';
import {
  type CustomActionContext,
  resolveIcon,
  runCustomAction,
} from '@/components/configurable-table/customButtonActions';
import type { CustomButtonMenuItem } from '@/components/configurable-table/ListActionsMenu';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { BulkChangeModal } from '@/components/list-actions/BulkChangeModal';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { runConflictText } from '@/lib/record-procedures/run-texts';
import { extractRunConflict, type RunConflict } from '@/lib/record-procedures/runs-api';
import { useMyModules } from '@/lib/use-my-modules';

import { actionRequiresSelection, resolveListActions } from './resolve';

/** Lista nie zna układów formularzy, więc nie zna napisów przycisków SQL — zadanie nazywa nazwa procedury. */
const NO_BUTTON_LABELS: ReadonlyMap<string, string> = new Map();

/**
 * Odrzucenia rozgłoszenia akcji (usuń / archiwizuj / wyślij…) spowodowane blokadą rekordu na czas zadania
 * przycisku SQL (409 `RECORD_LOCKED_BY_RUN`) — z `Promise.allSettled`. Pozostałe błędy nie mają tu opisu.
 */
function lockedRejections(results: readonly PromiseSettledResult<unknown>[]): RunConflict[] {
  const conflicts: RunConflict[] = [];
  for (const result of results) {
    if (result.status !== 'rejected') continue;
    const conflict = extractRunConflict(result.reason);
    if (conflict?.code === 'RECORD_LOCKED_BY_RUN') conflicts.push(conflict);
  }
  return conflicts;
}

/** Akcje wymagające potwierdzenia. „Usuń" (miękkie) pyta; archiwizacja = odwracalna flaga, bez pytania. */
const CONFIRM_ACTIONS = new Set<ListActionId>(['delete']);
/**
 * Akcje pominięte w kebabie POJEDYNCZEGO (żywego) rekordu. `unarchive` = „przywróć z
 * archiwum", bez sensu na żywym wierszu (dotyczy zaznaczenia zarchiwizowanych). Usuń,
 * archiwizuj, eksport i akcje modułowe (KSeF/FK) na jednym rekordzie MAJĄ sens — zostają.
 */
const ROW_KEBAB_EXCLUDE = new Set<ListActionId>(['unarchive']);
/**
 * Akcje, które NIE działają generykiem — potrzebują handlera strony (endpoint eksportu
 * ma niespójny kontrakt per encja; import to modal strony). Gdy strona nie poda handlera,
 * akcja jest widoczna, ale wyszarzona „wkrótce" (decyzja Eugenii — żeby nie umknęła).
 */
const NEEDS_HANDLER = new Set<ListActionId>([
  'export_csv',
  'export_list_csv',
  'export_excel',
  'import',
  'decree',
]);

/** Akcja WŁASNA tenanta zmapowana na pozycję menu (PHASE 25 Faza 2, T25F2.6). */
export interface CustomListActionItem {
  id: string;
  name: string;
  /** Nazwa ikony Lucide z definicji (fallback w rendererze). */
  icon: string;
  requireConfirm: boolean;
}

export interface UseListActionsOptions {
  /** Kartoteka listy (klucz osi kategorii lub `'generic'` dla list modułów). */
  kartotekaKey: ListActionKartotekaKey;
  /** Klasa dokumentu — tylko dla kartoteki `documents` (zawęża KSeF/FK). */
  documentClass?: string;
  /** Baza URL dla `:entity` w ścieżkach katalogu (np. `contractors`). */
  entity: string;
  /**
   * Slug kartoteki systemowej dla run-endpointu akcji WŁASNYCH (np. `sale_invoices`).
   * Route/`entity` bywa z myślnikiem (`sale-invoices`), a slug modułu z podkreśleniem —
   * domyślnie = `entity` (zgadza się dla większości kartotek systemowych; dokumenty
   * podają jawnie klasę → slug modułu). Bez tego custom akcje nie znajdą fizycznej tabeli.
   */
  moduleSlug?: string;
  /** Klucz React Query do inwalidacji po akcji / odświeżeniu. */
  queryKey: QueryKey;
  /**
   * Klucz cache distinct-values (dropdowny filtrów kolumn) do inwalidacji po akcji.
   * Domyślnie `['distinct-values', entity]`; podaj OSOBNO, gdy lista trzyma distinct
   * pod innym kluczem niż baza URL — faktury (route `sale-invoices`, distinct
   * `['distinct-values','sale_invoices']`) albo moduły (`['distinct-values', tableId]`).
   * Bez tego dropdowny filtrów zostają nieświeże do `staleTime` (konwencja PR 1712).
   */
  distinctValuesKey?: QueryKey;
  /** Zaznaczone rekordy (stan strony). */
  selectedIds: string[];
  /** Setter zaznaczenia — prawy klik na niezaznaczonym wierszu zaznacza go. */
  setSelectedIds: (ids: string[]) => void;
  /** Akcje włączone w layoucie (`ListConfig.listActions`); brak = zestaw domyślny. */
  listActionsConfig?: readonly ListActionConfig[];
  /**
   * Własne handlery per akcja (escape-hatch dla niespójnych endpointów / akcji
   * klienckich). Gdy podany — użyty ZAMIAST generyka (po potwierdzeniu/bramce).
   */
  handlers?: Partial<Record<ListActionId, (ids: string[]) => void | Promise<void>>>;
  /**
   * Bramka kontekstowa — zwraca powód (do toasta) blokady akcji, albo null gdy OK.
   * Np. tryb „Wszystkie firmy" blokuje archiwizację/import.
   */
  guard?: (actionId: ListActionId, ids: string[]) => string | null;
  /**
   * Dodatkowe akcje do oznaczenia jako „wkrótce" (widoczne, ale wyszarzone, bez
   * wykonania) — dla list, na których dana akcja katalogu nie ma jeszcze endpointu
   * (np. Kontakty kontrahentów nie mają trasy `restore`). Domyślnie „wkrótce" liczy
   * tylko akcje z `NEEDS_HANDLER` bez podpiętego handlera; ta opcja rozszerza zbiór
   * o akcje, których dana lista świadomie nie obsługuje. Trzyma UX „pokaż, nie chowaj".
   */
  unavailableActionsExtra?: readonly ListActionId[];
  /**
   * Akcje ZBRAMKOWANE brakiem konfiguracji (np. „Wyślij do KSeF" bez połączenia KSeF,
   * „Dekretuj" bez planu kont / szablonu / procedury). Renderowane jako dostępne, ale
   * z chipem „Konfiguracja"; klik NIE wykonuje akcji, tylko woła `onGatedClick` (modal
   * naprowadzający zamiast suchego błędu). Różne od „wkrótce" (brak handlera) i od `guard`
   * (toast blokady kontekstowej).
   */
  gatedActions?: readonly ListActionId[];
  /** Wywoływane po kliknięciu zbramkowanej akcji — otwórz modal naprowadzający. */
  onGatedClick?: (actionId: ListActionId, ids: string[]) => void;
  /**
   * Czy lista pokazuje właśnie rekordy ZARCHIWIZOWANE (toggle „Pokaż zarchiwizowane").
   * Steruje widocznością pary archiwum: w widoku aktywnych chowamy „Przywróć z archiwum"
   * (nie ma czego przywracać), w widoku archiwum chowamy „Archiwizuj". Domyślnie (brak /
   * false) = widok aktywnych. Dotyczy tylko dokumentów (jedyne z archiwum); dla pozostałych
   * kartotek bez znaczenia (nie mają tych akcji).
   */
  archivedView?: boolean;
  /**
   * Przełącznik „Pokaż zarchiwizowane" WBUDOWANY w menu Akcje (zamiast osobnego przycisku
   * w pasku nad listą). Podany → w menu pojawia się pozycja przełączająca widok
   * aktywne↔zarchiwizowane. Brak → menu bez tej pozycji (kartoteki bez archiwum).
   */
  archivedToggle?: { active: boolean; onToggle: () => void };
  /**
   * Własne przyciski z Kreatora Listy (`ListConfig.customActionButtons`). Hook rozkłada je
   * na wszystkie wejścia listy: „Pasek narzędzi" → obok dropdowna „Akcje", „Akcja wiersza"
   * → kebab wiersza + prawy klik (`{id}` w ścieżce/adresie), „Akcje masowe" → sekcja „na
   * zaznaczonych" (`{ids}`). Jedno miejsce zamiast osobnego wpięcia na każdej liście.
   */
  customButtons?: readonly CustomActionButton[];
  testIdPrefix?: string;
}

/** Własny przycisk z Kreatora Listy → pozycja menu „Akcje". */
function toMenuItem(b: CustomActionButton): CustomButtonMenuItem {
  return { id: b.id, label: b.label, icon: b.icon };
}

/** Widoczność pary akcji archiwum wg trybu widoku (aktywne vs zarchiwizowane). */
function visibleInArchiveView(id: ListActionId, archivedView: boolean): boolean {
  if (id === 'unarchive') return archivedView;
  if (id === 'archive') return !archivedView;
  return true;
}

export interface UseListActionsResult {
  /** Props do dropdowna „Akcje" nad listą. */
  menuProps: {
    selectionActions: ListActionDef[];
    generalActions: ListActionDef[];
    selectedCount: number;
    unavailableActions: Set<ListActionId>;
    gatedActions: Set<ListActionId>;
    /** Akcje WŁASNE tenanta dla tej kartoteki (poniżej wbudowanych, sekcja „na zaznaczonych"). */
    customActions: CustomListActionItem[];
    onRun: (actionId: ListActionId) => void;
    onRunCustom: (id: string) => void;
    /** Przełącznik „Pokaż zarchiwizowane" w menu Akcje (gdy lista ma archiwum). */
    archivedToggle?: { active: boolean; onToggle: () => void };
    bulkButtons: CustomButtonMenuItem[];
    rowButtons: CustomButtonMenuItem[];
    onRunButton: (id: string) => void;
    toolbar: React.ReactNode;
    testIdPrefix: string;
  };
  /** Handler prawego kliknięcia wiersza (`ConfigurableTable.onRowContextMenu`). */
  onRowContextMenu: (e: React.MouseEvent, row: { id: string }) => void;
  /** Pozycje kebaba wiersza z katalogu (pod dividerem) — dołóż do `RowActionsMenu.items`. */
  rowCatalogActionItems: (rowId: string) => RowActionItem[];
  /**
   * Same własne przyciski „Akcja wiersza" (bez akcji katalogu) — dla list z własnym
   * kebabem, które nie używają `rowCatalogActionItems` (Pracownicy, Użytkownicy).
   */
  rowButtonItems: (rowId: string) => RowActionItem[];
  /** Nakładki do wyrenderowania w drzewie strony (menu kontekstowe + dialog potwierdzenia). */
  overlays: React.ReactNode;
}

export function useListActions(opts: UseListActionsOptions): UseListActionsResult {
  const {
    kartotekaKey,
    documentClass,
    entity,
    moduleSlug,
    queryKey,
    distinctValuesKey,
    selectedIds,
    setSelectedIds,
    listActionsConfig,
    handlers,
    guard,
    unavailableActionsExtra,
    gatedActions,
    onGatedClick,
    archivedView = false,
    archivedToggle,
    customButtons,
    testIdPrefix = 'list',
  } = opts;
  const navigate = useNavigate();

  const gatedSet = useMemo(() => new Set<ListActionId>(gatedActions ?? []), [gatedActions]);

  const { t, i18n } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { me } = useTenantAuth();
  const { data: myModules } = useMyModules();

  const activeModuleCodes = useMemo(
    () => (myModules?.subscriptions ?? []).map((s) => s.code),
    [myModules?.subscriptions],
  );

  const { selectionActions: resolvedSelection, generalActions: resolvedGeneral } = useMemo(
    () =>
      resolveListActions({
        kartotekaKey,
        documentClass,
        enabledActions: listActionsConfig,
        activeModuleCodes,
        permissions: me?.permissions ?? [],
        isSuperUser: me?.user.isSuperUser === true,
      }),
    [
      kartotekaKey,
      documentClass,
      listActionsConfig,
      activeModuleCodes,
      me?.permissions,
      me?.user.isSuperUser,
    ],
  );

  // Widok archiwum steruje parą archiwum: aktywne → bez „Przywróć z archiwum",
  // archiwum → bez „Archiwizuj". Filtrujemy raz, w jednym miejscu (menu + kebab + ctx).
  const selectionActions = useMemo(
    () => resolvedSelection.filter((a) => visibleInArchiveView(a.id, archivedView)),
    [resolvedSelection, archivedView],
  );
  const generalActions = useMemo(
    () => resolvedGeneral.filter((a) => visibleInArchiveView(a.id, archivedView)),
    [resolvedGeneral, archivedView],
  );

  // Akcje widoczne, ale niepodpięte na tej liście (wymagają handlera, brak) → „wkrótce".
  const unavailableActions = useMemo(() => {
    const s = new Set<ListActionId>();
    for (const def of [...selectionActions, ...generalActions]) {
      if (NEEDS_HANDLER.has(def.id) && !handlers?.[def.id]) s.add(def.id);
    }
    // Akcje, których dana lista świadomie nie obsługuje (brak endpointu) — też „wkrótce".
    for (const id of unavailableActionsExtra ?? []) s.add(id);
    return s;
  }, [selectionActions, generalActions, handlers, unavailableActionsExtra]);

  // Akcje WŁASNE tenanta dla tej kartoteki (T25F2.6) — scalane pod wbudowanymi w menu.
  // Klucz współdzielony z ekranem Ustawienia→Akcje (dedupe + auto-odświeżenie po zapisie).
  const customDefsQuery = useQuery({
    queryKey: ['list-action-definitions', kartotekaKey],
    queryFn: async () => {
      const res = await api.get<{ definitions: ListActionDefinitionDTO[] }>(
        '/list-action-definitions',
        { params: { kartoteka: kartotekaKey } },
      );
      return res.data.definitions;
    },
  });

  const customActions = useMemo<CustomListActionItem[]>(
    () =>
      (customDefsQuery.data ?? [])
        .filter((d) => d.isActive)
        .map((d) => ({
          id: d.id,
          name: d.name,
          icon: d.icon ?? 'Zap',
          requireConfirm: d.requireConfirm === true,
        })),
    [customDefsQuery.data],
  );

  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; ids: string[] } | null>(null);
  const [pending, setPending] = useState<{ actionId: ListActionId; ids: string[] } | null>(null);
  // Potwierdzenie akcji WŁASNEJ z flagą „wymagaj potwierdzenia".
  const [customPending, setCustomPending] = useState<{ id: string; ids: string[] } | null>(null);
  // „Zmień zbiorczo" (akcja jednorazowa) — modal wbudowany w hook (działa na każdej liście).
  const [bulkChange, setBulkChange] = useState<{ ids: string[] } | null>(null);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({
      queryKey: distinctValuesKey ?? ['distinct-values', entity],
    });
  }, [queryClient, queryKey, entity, distinctValuesKey]);

  // Własne przyciski z Kreatora Listy — rozkład po pozycjach (kolejność z Kreatora).
  const buttonsByPosition = useMemo(() => {
    const sorted = [...(customButtons ?? [])].sort((a, b) => a.order - b.order);
    return {
      all: sorted,
      toolbar: sorted.filter((b) => b.position === 'toolbar'),
      row: sorted.filter((b) => b.position === 'row_actions'),
      bulk: sorted.filter((b) => b.position === 'bulk_actions'),
    };
  }, [customButtons]);
  const buttonCtx = useMemo<CustomActionContext>(
    () => ({ navigate, refresh: invalidate }),
    [navigate, invalidate],
  );
  const runButton = useCallback(
    (id: string, ids: readonly string[]) => {
      const btn = buttonsByPosition.all.find((b) => b.id === id);
      if (!btn) return;
      runCustomAction(btn.action, buttonCtx, { ids });
    },
    [buttonsByPosition, buttonCtx],
  );
  const bulkButtonItems = useMemo(
    () => buttonsByPosition.bulk.map(toMenuItem),
    [buttonsByPosition],
  );
  const rowButtonMenuItems = useMemo(
    () => buttonsByPosition.row.map(toMenuItem),
    [buttonsByPosition],
  );
  const rowButtonItems = useCallback(
    (rowId: string): RowActionItem[] =>
      buttonsByPosition.row.map((btn) => ({
        key: `btn-${btn.id}`,
        label: btn.label,
        icon: resolveIcon(btn.icon) ?? undefined,
        onSelect: () => runButton(btn.id, [rowId]),
        testId: `${testIdPrefix}-row-${rowId}-button-${btn.id}`,
      })),
    [buttonsByPosition, runButton, testIdPrefix],
  );
  const toolbar =
    buttonsByPosition.toolbar.length > 0 ? (
      <CustomActionButtonBar
        buttons={buttonsByPosition.toolbar}
        ctx={buttonCtx}
        testIdPrefix={`${testIdPrefix}-toolbar`}
      />
    ) : null;

  /** Podstaw `:entity`/`:id` w szablonie ścieżki katalogu (zachowuje query-string). */
  const resolvePath = useCallback(
    (path: string, id?: string) => path.replace(':entity', entity).replace(':id', id ?? ''),
    [entity],
  );

  /** Surowe wykonanie wg „sposobu wykonania" z katalogu / handlera strony. */
  const runDispatch = useCallback(
    async (def: ListActionDef, ids: string[]) => {
      const override = handlers?.[def.id];
      if (override) {
        await override(ids);
        return;
      }
      const d = def.dispatch;
      if (d.kind === 'client') {
        if (d.op === 'refresh') invalidate();
        // open_import bez handlera = brak akcji (strona nie podpięła modala importu).
        return;
      }
      if (d.kind === 'download') {
        const res = await api.post(
          resolvePath(d.path),
          { [d.idsKey]: ids },
          { responseType: 'blob' },
        );
        const url = URL.createObjectURL(res.data as Blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${entity}-export-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        return;
      }
      if (d.kind === 'bulk') {
        await api.post(resolvePath(d.path), { [d.idsKey]: ids });
        invalidate();
        return;
      }
      // fanout — rozgłoszenie po ID na per-rekordowy endpoint.
      const results = await Promise.allSettled(
        ids.map((id) => {
          const url = resolvePath(d.path, id);
          return d.method === 'DELETE' ? api.delete(url) : api.post(url);
        }),
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = ids.length - ok;
      invalidate();
      // Rekordy z zadaniem przycisku SQL w toku odrzucają zmianę (409 `RECORD_LOCKED_BY_RUN`): zamiast samej
      // liczby nieudanych podajemy powód — jedno zadanie opisane (napis/godzina/osoba), kilka — ogólnie.
      // Tylko gdy WSZYSTKIE porażki to blokada — przy porażkach z innych przyczyn opis zadania sugerowałby,
      // że to ono zablokowało całą nieudaną część.
      const locked = lockedRejections(results);
      let lockedReason: string | undefined;
      if (locked.length === failed && locked.length === 1 && locked[0]) {
        lockedReason = runConflictText(locked[0], NO_BUTTON_LABELS, t, i18n.language);
      } else if (locked.length === failed && locked.length > 1) {
        lockedReason = t('recordProcedureRuns.errors.lockedMany', { count: locked.length });
      }
      toast.show({
        variant: failed > 0 ? 'error' : 'success',
        title:
          failed > 0
            ? t('tenant.listActions.toast.partial', { ok, failed })
            : t('tenant.listActions.toast.done', { count: ok }),
        description: lockedReason,
      });
    },
    [handlers, invalidate, resolvePath, entity, toast, t, i18n],
  );

  /** Wykonanie akcji + czyszczenie zaznaczenia po akcji na zaznaczeniu. */
  const execute = useCallback(
    async (def: ListActionDef, ids: string[]) => {
      await runDispatch(def, ids);
      if (actionRequiresSelection(def, kartotekaKey)) setSelectedIds([]);
    },
    [runDispatch, setSelectedIds, kartotekaKey],
  );

  /** Wykonanie akcji WŁASNEJ na zaznaczonych (T25F2.6) — dispatch na run-endpoint. */
  const executeCustom = useCallback(
    async (id: string, ids: string[]) => {
      try {
        const res = await api.post<{ total: number; ok: number; failed: number }>(
          `/list-action-definitions/${id}/run`,
          { kartotekaKey, slug: moduleSlug ?? entity, selectedIds: ids },
        );
        const { ok = 0, failed = 0 } = res.data ?? {};
        invalidate();
        setSelectedIds([]);
        toast.show({
          variant: failed > 0 ? 'error' : 'success',
          title:
            failed > 0
              ? t('tenant.listActions.toast.partial', { ok, failed })
              : t('tenant.listActions.toast.done', { count: ok }),
        });
      } catch {
        toast.show({ variant: 'error', title: t('tenant.listActions.errorGeneric') });
      }
    },
    [kartotekaKey, moduleSlug, entity, invalidate, setSelectedIds, toast, t],
  );

  const runCustom = useCallback(
    (id: string, ids: string[]) => {
      if (ids.length === 0) return;
      const def = customActions.find((c) => c.id === id);
      if (!def) return;
      if (def.requireConfirm) {
        setCustomPending({ id, ids });
        return;
      }
      void executeCustom(id, ids);
    },
    [customActions, executeCustom],
  );

  const runAction = useCallback(
    (actionId: ListActionId, ids: string[]) => {
      const def = LIST_ACTION_CATALOG[actionId];
      if (!def) return;
      if (unavailableActions.has(actionId)) return; // „wkrótce" — nic nie rób
      const reason = guard?.(actionId, ids);
      if (reason) {
        toast.show({ variant: 'error', title: reason });
        return;
      }
      // Akcje na zaznaczeniu bez zaznaczenia (poza ogólnymi) — nic nie rób.
      if (actionRequiresSelection(def, kartotekaKey) && ids.length === 0) return;
      // „Zmień zbiorczo" — otwórz modal wsadowy (nie wykonuje się generykiem).
      if (actionId === 'bulk_change') {
        setBulkChange({ ids });
        return;
      }
      // Brak konfiguracji → zamiast wykonania otwórz modal naprowadzający.
      if (gatedSet.has(actionId)) {
        onGatedClick?.(actionId, ids);
        return;
      }
      if (CONFIRM_ACTIONS.has(actionId)) {
        setPending({ actionId, ids });
        return;
      }
      void execute(def, ids);
    },
    [guard, toast, execute, unavailableActions, gatedSet, onGatedClick, kartotekaKey],
  );

  const onRowContextMenu = useCallback(
    (e: React.MouseEvent, row: { id: string }) => {
      e.preventDefault();
      const onSelection = selectedIds.length > 0 && selectedIds.includes(row.id);
      const ids = onSelection ? selectedIds : [row.id];
      if (!onSelection) setSelectedIds([row.id]);
      setCtxMenu({ x: e.clientX, y: e.clientY, ids });
    },
    [selectedIds, setSelectedIds],
  );

  const rowCatalogActionItems = useCallback(
    (rowId: string): RowActionItem[] => {
      // Kebab pomija: `restore` (bez sensu per żywy wiersz) + akcje „wkrótce" (niepodpięte).
      const items = selectionActions.filter(
        (a) => !ROW_KEBAB_EXCLUDE.has(a.id) && !unavailableActions.has(a.id),
      );
      const builtIn = items.map((a, i) => ({
        key: `la-${a.id}`,
        label: t(a.labelKey),
        icon: listActionLucideIcon(a.icon),
        dividerBefore: i === 0,
        onSelect: () => runAction(a.id, [rowId]),
        testId: `${testIdPrefix}-row-${rowId}-action-${a.id}`,
      }));
      // Akcje WŁASNE tenanta (T25F2.6) — pod wbudowanymi.
      const custom = customActions.map((c, i) => ({
        key: `lac-${c.id}`,
        label: c.name,
        icon: listActionLucideIcon(c.icon),
        dividerBefore: i === 0 && builtIn.length > 0,
        onSelect: () => runCustom(c.id, [rowId]),
        testId: `${testIdPrefix}-row-${rowId}-custom-${c.id}`,
      }));
      // Własne przyciski „Akcja wiersza" z Kreatora Listy — nad akcjami katalogu.
      return [...rowButtonItems(rowId), ...builtIn, ...custom];
    },
    [
      selectionActions,
      unavailableActions,
      customActions,
      t,
      runAction,
      runCustom,
      rowButtonItems,
      testIdPrefix,
    ],
  );

  const pendingDef = pending ? LIST_ACTION_CATALOG[pending.actionId] : null;

  const overlays = (
    <>
      {ctxMenu && (
        <ListActionsContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          testIdPrefix={testIdPrefix}
          selectionActions={selectionActions}
          generalActions={generalActions}
          selectedCount={ctxMenu.ids.length}
          unavailableActions={unavailableActions}
          gatedActions={gatedSet}
          customActions={customActions}
          onRunCustom={(id) => runCustom(id, ctxMenu.ids)}
          bulkButtons={bulkButtonItems}
          rowButtons={rowButtonMenuItems}
          onRunButton={(id) => runButton(id, ctxMenu.ids)}
          onRun={(actionId) => runAction(actionId, ctxMenu.ids)}
          onClose={() => setCtxMenu(null)}
        />
      )}
      {pending && pendingDef && (
        <ConfirmDialog
          open
          variant={pendingDef.destructive ? 'danger' : 'primary'}
          confirmLabel={t(pendingDef.labelKey)}
          title={t(
            pending.actionId === 'archive'
              ? 'tenant.listActions.confirm.archiveTitle'
              : 'tenant.listActions.confirm.deleteTitle',
          )}
          message={t('tenant.listActions.confirm.message', { count: pending.ids.length })}
          onConfirm={() => {
            const p = pending;
            setPending(null);
            if (p) void execute(pendingDef, p.ids);
          }}
          onClose={() => setPending(null)}
        />
      )}
      {customPending && (
        <ConfirmDialog
          open
          variant="primary"
          confirmLabel={
            customActions.find((c) => c.id === customPending.id)?.name ?? t('common.ok')
          }
          title={customActions.find((c) => c.id === customPending.id)?.name ?? ''}
          message={t('tenant.listActions.confirm.message', { count: customPending.ids.length })}
          onConfirm={() => {
            const p = customPending;
            setCustomPending(null);
            if (p) void executeCustom(p.id, p.ids);
          }}
          onClose={() => setCustomPending(null)}
        />
      )}
      {bulkChange && (
        <BulkChangeModal
          open
          kartotekaKey={kartotekaKey}
          slug={moduleSlug ?? entity}
          selectedIds={bulkChange.ids}
          onClose={() => setBulkChange(null)}
          onDone={() => {
            invalidate();
            setSelectedIds([]);
          }}
        />
      )}
    </>
  );

  return {
    menuProps: {
      selectionActions,
      generalActions,
      selectedCount: selectedIds.length,
      unavailableActions,
      gatedActions: gatedSet,
      customActions,
      onRunCustom: (id) => runCustom(id, selectedIds),
      onRun: (actionId) => runAction(actionId, selectedIds),
      archivedToggle,
      bulkButtons: bulkButtonItems,
      rowButtons: rowButtonMenuItems,
      onRunButton: (id) => runButton(id, selectedIds),
      toolbar,
      testIdPrefix,
    },
    onRowContextMenu,
    rowCatalogActionItems,
    rowButtonItems,
    overlays,
  };
}
